import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  ShieldCheck, 
  FileText, 
  CreditCard, 
  RefreshCw, 
  ArrowLeft, 
  CheckCircle2, 
  Lock, 
  HelpCircle,
  Stethoscope,
  Building2,
  Mail,
  PhoneCall
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function PolicyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('privacy');

  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: Lock },
    { id: 'terms', label: 'Terms of Service', icon: FileText },
    { id: 'refunds', label: 'Cancellation & Refunds', icon: RefreshCw },
    { id: 'payments', label: 'Payment Terms', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-teal-500 selection:text-white">
      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
          <div 
            onClick={() => navigate('/')} 
            className="flex items-center gap-3 cursor-pointer group"
          >
            <div className="h-9 w-9 sm:h-10 sm:w-10 bg-primary rounded-full flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-heading font-bold text-primary tracking-tight">
              MRcall
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="rounded-full border-slate-200 hover:bg-slate-100 text-slate-700 text-xs sm:text-sm px-3 sm:px-4"
            >
              <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" />
              Back
            </Button>
            {user ? (
              <Button
                onClick={() => {
                  if (user.role === 'doctor') navigate('/doctor');
                  else if (user.role === 'mr') navigate('/mr');
                  else if (user.role === 'admin') navigate('/admin');
                }}
                className="rounded-full bg-primary hover:bg-teal-800 text-white font-semibold text-xs sm:text-sm px-4 sm:px-6 shadow"
              >
                Dashboard
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="rounded-full bg-primary hover:bg-teal-800 text-white font-semibold text-xs sm:text-sm px-4 sm:px-6 shadow"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 text-white py-12 sm:py-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-teal-200 text-xs sm:text-sm font-medium mb-4 backdrop-blur-sm border border-white/10">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            Legal Guidelines & User Protection
          </div>
          <h1 className="text-3xl sm:text-5xl font-heading font-bold tracking-tight text-white mb-4">
            Platform Policies & Terms
          </h1>
          <p className="text-base sm:text-lg text-teal-100/90 leading-relaxed max-w-2xl mx-auto">
            Transparency, patient data security, and seamless appointment booking between Doctors and Medical Representatives.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mb-8 sm:mb-12 border-b border-slate-200 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all ${
                  isActive
                    ? 'bg-primary text-white shadow-md shadow-teal-700/20'
                    : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200'
                }`}
              >
                <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-10 max-w-4xl mx-auto">
          {/* 1. Privacy Policy */}
          {activeTab === 'privacy' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">Data Protection</span>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mt-1 mb-3">
                  Privacy Policy
                </h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Last Updated: August 2026. MRcall is committed to safeguarding your personal details and professional credentials.
                </p>
              </div>

              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-teal-600" />
                    Information We Collect
                  </h3>
                  <ul className="space-y-2 text-slate-600 text-sm sm:text-base pl-7 list-disc">
                    <li><strong>Doctor Credentials:</strong> Name, specialization, qualification, clinic address, contact number, visiting card, and photo.</li>
                    <li><strong>MR Details:</strong> Name, pharmaceutical company, division, visiting card, brochure details, and profile photo.</li>
                    <li><strong>Booking Records:</strong> Appointment timestamps, notes, and slot requests.</li>
                  </ul>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Lock className="h-5 w-5 text-teal-600" />
                    How We Use Your Data
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Data collected is strictly used for verifying healthcare professionals, matching Medical Representatives with Doctors, sending booking notifications (SMS/Email), and managing subscription plans.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-teal-600" />
                    Confidentiality & No Third-Party Sales
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    We <strong>never sell or rent</strong> doctor lists or MR personal details to external marketing agencies or third parties. All stored credentials are encrypted and accessed solely for appointment scheduling.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 2. Terms of Service */}
          {activeTab === 'terms' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">Rules & Conduct</span>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mt-1 mb-3">
                  Terms of Service
                </h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  By accessing MRcall, Doctors and Medical Representatives agree to abide by professional code of conduct.
                </p>
              </div>

              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Stethoscope className="h-5 w-5 text-teal-600" />
                    Doctor Guidelines
                  </h3>
                  <ul className="space-y-2 text-slate-600 text-sm sm:text-base pl-7 list-disc">
                    <li>Maintain accurate weekly recurring time slots for rep visits.</li>
                    <li>Review, approve, or reschedule appointment requests in a timely manner.</li>
                    <li>Report any non-genuine or inappropriate booking requests.</li>
                  </ul>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-teal-600" />
                    Medical Representative (MR) Conduct
                  </h3>
                  <ul className="space-y-2 text-slate-600 text-sm sm:text-base pl-7 list-disc">
                    <li>Book appointments strictly within available doctor slots.</li>
                    <li>Respect clinic rules and punctual timing during visits.</li>
                    <li>Provide verified company affiliation and product brochures upon request.</li>
                    <li>Adhere to the 5 free appointments/month limit or upgrade to Premium for unlimited access.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* 3. Cancellation & Refunds */}
          {activeTab === 'refunds' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">Fair Policy</span>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mt-1 mb-3">
                  Cancellation & Refund Policy
                </h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  We strive for complete satisfaction with your MRcall Premium subscription.
                </p>
              </div>

              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-teal-50/60 border border-teal-200">
                  <h3 className="text-lg font-heading font-bold text-teal-950 mb-2 flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-teal-600" />
                    7-Day Subscription Guarantee
                  </h3>
                  <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                    If you upgrade to <strong>Monthly (₹199)</strong> or <strong>Yearly (₹1599)</strong> Premium and wish to cancel within <strong>7 days of purchase</strong> without using premium booking privileges, you are eligible for a 100% full refund.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    Appointment Cancellation
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    MRs and Doctors can cancel or reschedule appointments at any time prior to the slot time. Cancellations update doctor capacity instantly.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-teal-600" />
                    How to Request a Refund
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    Email our support desk at <a href="mailto:support@mrcall.in" className="text-primary font-semibold underline">support@mrcall.in</a> with your registered email and payment ID. Refunds are processed to the original payment source within 3–5 business days.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 4. Payment Terms */}
          {activeTab === 'payments' && (
            <div className="space-y-8 animate-fadeIn">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary">Billing & Subscriptions</span>
                <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 mt-1 mb-3">
                  Payment Terms
                </h2>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Flexible pricing options tailored for individual reps and medical affairs teams.
                </p>
              </div>

              <div className="space-y-6">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-teal-600" />
                    Subscription Plans
                  </h3>
                  <ul className="space-y-3 text-slate-700 text-sm sm:text-base">
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-slate-900">Free Tier:</span> 
                      <span>5 free doctor appointments per month. Resets automatically on the 1st of every month.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-primary">Monthly Premium (₹199/mo):</span> 
                      <span>Unlimited appointments per month with priority doctor slot booking.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-semibold text-emerald-700">Yearly Premium (₹1599/yr):</span> 
                      <span>Best value plan with 33% annual savings (₹133/month equivalent).</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                  <h3 className="text-lg font-heading font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-teal-600" />
                    Payment Processing & Security
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    All digital transactions are powered by <strong>Razorpay Payment Gateway</strong> supporting UPI (Google Pay, PhonePe, Paytm), Credit/Debit Cards, Net Banking, and Wallet payments with 256-bit SSL encryption. MRcall does not store card numbers or UPI PINs.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Support Banner */}
        <div className="mt-12 bg-slate-900 text-white rounded-2xl p-6 sm:p-10 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
          <div>
            <h3 className="text-xl sm:text-2xl font-heading font-bold mb-2">Have questions about our policies?</h3>
            <p className="text-slate-300 text-sm sm:text-base">Our support team is ready to help healthcare professionals and reps.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <a 
              href="mailto:support@mrcall.in" 
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-teal-700 text-white font-semibold text-sm transition-all shadow"
            >
              <Mail className="h-4 w-4" />
              Contact Support
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 text-slate-400 py-10 px-4 sm:px-6 lg:px-12 text-center text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            <span className="font-heading font-bold text-white text-base">MRcall</span>
          </div>
          <p>&copy; 2026 MRcall Platform. All rights reserved. Designed for Indian Healthcare Professionals.</p>
        </div>
      </footer>
    </div>
  );
}
